import { useContext, useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { GET_INSPECTIONS, GET_MONTHLY_VEHICLE_DEFECTS_REPORT } from "../../graphql/queries";
import ViewRecordForm from "./ViewRecordForm";
import usePagination from "../../hooks/usePagination";
import UserContext from "../../context/UserContext";
import { Icon } from "@iconify/react";
import { statusClass } from "../../helpers/utils";
import RecordReviewForm from "./RecordReviewForm";
import { getFuzzyDate } from "../../helpers/dates";
import { downloadExport } from "../../helpers/exportDownload";

const TRACKER_OPTIONS = [
  { value: "", label: "Tracker: any" },
  { value: "pending", label: "Tracker: Pending" },
  { value: "overdue", label: "Tracker: Overdue" },
];

const formatMonthLabel = (yyyymm: string) => {
  if (!yyyymm || yyyymm.length !== 6) return yyyymm;
  const y = Number(yyyymm.slice(0, 4));
  const m = Number(yyyymm.slice(4, 6)) - 1;
  return new Date(y, m).toLocaleString("en-US", { month: "short", year: "numeric" });
};

const STATUS_FILTER = ["Access_Denied", "Access_Permitted"];

function RecordsWithDefectsList() {
  const userCtx = useContext(UserContext);
  const [viewRecords, setViewRecords] = useState<boolean>(false);
  const [reviewRecord, setReviewRecord] = useState<boolean>(false);
  const [selected, setSelected] = useState();
  const [totalRecords, setTotalRecords] = useState(1);
  const { Pagination, pageInfo, setPageInfo } = usePagination(8);
  const [getInspections, { loading }] = useLazyQuery(GET_INSPECTIONS, {
    fetchPolicy: "network-only",
  });
  const [getMonths] = useLazyQuery(GET_MONTHLY_VEHICLE_DEFECTS_REPORT, {
    fetchPolicy: "cache-and-network",
  });

  const [data, setData] = useState<any[]>([]);
  const [filter, setFilter] = useState("");
  const [month, setMonth] = useState("");
  const [trackerCategory, setTrackerCategory] = useState("");
  const [pendingFilter, setPendingFilter] = useState("");
  const [pendingMonth, setPendingMonth] = useState("");
  const [pendingTracker, setPendingTracker] = useState("");
  const [monthOptions, setMonthOptions] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);

  const isDirty =
    pendingFilter !== filter ||
    pendingMonth !== month ||
    pendingTracker !== trackerCategory;

  function applyFilters() {
    setFilter(pendingFilter.trim());
    setMonth(pendingMonth);
    setTrackerCategory(pendingTracker);
  }

  function clearFilters() {
    setPendingFilter("");
    setPendingMonth("");
    setPendingTracker("");
    setFilter("");
    setMonth("");
    setTrackerCategory("");
  }

  useEffect(() => {
    getMonths().then((res) => {
      const months = (res?.data?.getMonthlyVehicleDefects || [])
        .map((r: any) => r.month)
        .filter(Boolean)
        .sort()
        .reverse();
      setMonthOptions(months);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function buildVars(page: number) {
    const transporter = userCtx.user.transporter;
    const vars: any = {
      filter,
      page,
      limit: pageInfo.limit,
      status: STATUS_FILTER,
      month: month || undefined,
      trackerCategory: trackerCategory || undefined,
    };
    if (transporter) vars.transporterId = transporter.id;
    return vars;
  }

  async function handleExport() {
    setExporting(true);
    try {
      const transporter = userCtx.user.transporter;
      const body: any = {
        filter,
        status: STATUS_FILTER,
        month: month || undefined,
        tracker_category: trackerCategory || undefined,
      };
      if (transporter) body["transporter_id"] = transporter.id;
      await downloadExport("inspections", body);
    } finally {
      setExporting(false);
    }
  }

  function refresh(page: number = 1) {
    getInspections({ variables: buildVars(page) }).then((res) => {
      if (res.data) {
        const resData = res.data.getAllInspections;
        setTotalRecords(resData.totalRecords);
        setData(resData.records);
      }
    });
  }

  useEffect(() => {
    setPageInfo((prev) => ({ ...prev, page: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo.limit, filter, month, trackerCategory]);

  useEffect(() => {
    refresh(pageInfo.page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageInfo, filter, month, trackerCategory]);

  function displaySetup(s: any) {
    if (!s) return null;
    const name = s.fields?.find((f: any) => f.name === "name");
    if (name) return name.value;
  }

  const activeFilters = [filter, month, trackerCategory].filter(Boolean).length;

  return (
    <>
      <div className="px-2 py-2 flex flex-wrap items-center gap-3">
        <h3 className="text-base font-medium">
          List of records with defects
          {activeFilters > 0 && (
            <span className="text-xs text-slate-500 ml-2">
              ({activeFilters} filter{activeFilters > 1 ? "s" : ""})
            </span>
          )}
        </h3>
        <div className="flex-1" />
        <div className="flex gap-2 items-center">
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting || data.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 px-3 py-2 rounded text-white text-sm"
            title="Export currently filtered rows to Excel"
          >
            {exporting ? "Exporting..." : "Export to Excel"}
          </button>
        </div>
        {reviewRecord && <RecordReviewForm
          record={selected}
          onComplete={() => {
            refresh();
            setReviewRecord(false);
          }}
        />}
        {viewRecords && <ViewRecordForm open={viewRecords} record={selected} onComplete={() => setViewRecords(false)} />}
      </div>
      <div className="px-2 pb-2 flex flex-wrap items-center gap-2 border-b">
        <input
          type="search"
          value={pendingFilter}
          onChange={(e) => setPendingFilter(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              applyFilters();
            }
          }}
          placeholder="Search transporter / customer / driver / reg. no..."
          className="flex-1 min-w-[220px] px-3 py-2 border rounded-lg outline-1 outline-slate-400"
        />
        <select
          value={pendingMonth}
          onChange={(e) => setPendingMonth(e.target.value)}
          className="px-2 py-2 border rounded text-sm"
          title="Filter by month"
        >
          <option value="">Month: any</option>
          {monthOptions.map((m) => (
            <option key={m} value={m}>
              {formatMonthLabel(m)}
            </option>
          ))}
        </select>
        <select
          value={pendingTracker}
          onChange={(e) => setPendingTracker(e.target.value)}
          className="px-2 py-2 border rounded text-sm"
          title="Filter by tracker category"
        >
          {TRACKER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={applyFilters}
          disabled={!isDirty}
          className={`px-3 py-2 rounded text-white text-sm flex items-center gap-1 ${
            isDirty ? "bg-slate-700 hover:bg-slate-800" : "bg-slate-300"
          }`}
          title="Apply filters"
        >
          <Icon icon="material-symbols:search" width={16} height={16} />
          Search
        </button>
        {activeFilters > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            className="px-2 py-2 border rounded text-slate-600 hover:bg-slate-100 text-sm"
            title="Clear all filters"
          >
            Clear
          </button>
        )}
      </div>
      <div className="w-full p-2 overflow-y-auto table-wrap">
        <table className="table border-collapse border border-slate-400 table-auto w-full text-left">
          <thead className="bg-slate-100">
            <tr>
              <th className="border border-slate-300 px-2">#</th>
              <th className="border border-slate-300 px-2">Transporter/Customer</th>
              <th className="border border-slate-300 px-2">Site</th>
              <th className="border border-slate-300 px-2 whitespace-nowrap">Inspected</th>
              <th className="border border-slate-300 px-2 whitespace-nowrap">Reg. No.</th>
              <th className="border border-slate-300 px-2">License No</th>
              <th className="border border-slate-300 px-2">Status</th>
              <th className="border border-slate-300 px-2">Overdue</th>
              <th className="border border-slate-300 px-2"></th>
            </tr>
          </thead>
          <tbody className="text-xs">
            {loading ? (
              <tr><td colSpan={9}><p className="px-2 text-orange-400">Loading ...</p></td></tr>
            ) : !data || data.length === 0 ? (
              <tr><td colSpan={9}><p className="px-2">No data found</p></td></tr>
            ) : (
              data.map((i: any, idx: number) => {
                let overDue = i.defectsAge - 7;
                if (overDue < 0) overDue = 0;
                return (
                  <tr key={idx} className={`${statusClass(i.status)}`}>
                    <td className="border border-slate-300 px-2">{i.id}</td>
                    <td className="border border-slate-300 px-2">
                      {displaySetup(i.transporter) || displaySetup(i.customer)}
                    </td>
                    <td className="border border-slate-300 px-2">{displaySetup(i.site)}</td>
                    <td className="border border-slate-300 px-2">{getFuzzyDate(new Date(i.createdAt))}</td>
                    <td className="border border-slate-300 px-2">{i.vehicleReg}</td>
                    <td className="border border-slate-300 px-2">{i.licenseNo}</td>
                    <td className="border border-slate-300 px-2">{i.status}</td>
                    <td className="border border-slate-300 px-2 whitespace-nowrap">{overDue} day(s)</td>
                    <td className="border border-slate-300 px-2">
                      <div className="flex gap-2">
                        {(userCtx?.hasPermission("ViewDefects") || userCtx?.hasPermission("FixDefects")) && <button
                          onClick={() => {
                            setSelected(i);
                            setReviewRecord(true);
                          }}
                        >
                          <div className="flex items-center gap-1 rounded bg-orange-500 p-1 text-white"><Icon icon='material-symbols:checklist' width={24} height={24} /> Review</div>
                        </button>
                        }
                        {userCtx?.hasPermission("ViewRecords") && <button
                          onClick={() => {
                            setSelected(i);
                            setViewRecords(true);
                          }}
                        >
                          <div className="flex items-center gap-1 rounded bg-gray-500 p-1 text-white"><Icon icon='material-symbols:checklist' width={24} height={24} />Details</div>
                        </button>}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
      <Pagination totalRecords={totalRecords} />
    </>
  );
}

export default RecordsWithDefectsList;
